import { ScheduleError } from './types'

/**
 * Just enough of the .xlsx container to read one sheet.
 *
 * No library: a spreadsheet parser is a large dependency to carry into a
 * browser bundle for four columns of text, and this file is the one place the
 * app touches a vendor format, so it is cheaper to own it. The container is a
 * ZIP of XML — `DecompressionStream` inflates, `DOMParser` reads. Both are
 * platform APIs, so nothing here can drift the way a pinned parser would.
 *
 * Reads only what the daily report uses: shared strings, inline strings,
 * numbers and the date/time serials a differently configured export might
 * produce. Formulas are read as their cached result, which is what a report
 * export carries anyway.
 */

/** One row: column letter -> cell text, blanks omitted. */
export interface SheetRow {
  /** 1-based row number as the sheet states it, so a skip can be reported. */
  row: number
  cells: Record<string, string>
}

const ZIP_EOCD = 0x06054b50
const ZIP_CENTRAL = 0x02014b50

interface ZipEntry {
  name: string
  method: number
  compressedSize: number
  localHeaderOffset: number
}

/** Reads the first worksheet of a workbook. */
export async function readSheet(buffer: ArrayBuffer, fileName: string): Promise<SheetRow[]> {
  const entries = centralDirectory(new DataView(buffer), fileName)

  const sheetName = await firstSheetPath(buffer, entries, fileName)
  const sheetXml = await entryText(buffer, entries, sheetName, fileName)
  if (!sheetXml) throw new ScheduleError('unreadable_file', fileName)

  const sharedXml = await entryText(buffer, entries, 'xl/sharedStrings.xml', fileName)
  const shared = sharedXml ? sharedStrings(sharedXml, fileName) : []

  return rowsOf(sheetXml, shared, fileName)
}

/** Walks the central directory. Zip64 is not handled: report exports are small. */
function centralDirectory(view: DataView, fileName: string): ZipEntry[] {
  let eocd = -1
  const start = Math.max(0, view.byteLength - 66_000)
  for (let at = view.byteLength - 22; at >= start; at -= 1) {
    if (view.getUint32(at, true) === ZIP_EOCD) {
      eocd = at
      break
    }
  }
  if (eocd < 0) throw new ScheduleError('unreadable_file', fileName)

  const count = view.getUint16(eocd + 10, true)
  let at = view.getUint32(eocd + 16, true)
  const entries: ZipEntry[] = []
  const decoder = new TextDecoder()

  for (let index = 0; index < count; index += 1) {
    if (at + 46 > view.byteLength || view.getUint32(at, true) !== ZIP_CENTRAL) break
    const method = view.getUint16(at + 10, true)
    const compressedSize = view.getUint32(at + 20, true)
    const nameLength = view.getUint16(at + 28, true)
    const extraLength = view.getUint16(at + 30, true)
    const commentLength = view.getUint16(at + 32, true)
    const localHeaderOffset = view.getUint32(at + 42, true)
    const name = decoder.decode(new Uint8Array(view.buffer, view.byteOffset + at + 46, nameLength))
    entries.push({ name, method, compressedSize, localHeaderOffset })
    at += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

async function entryText(
  buffer: ArrayBuffer,
  entries: ZipEntry[],
  name: string,
  fileName: string,
): Promise<string | null> {
  const entry = entries.find((candidate) => candidate.name === name)
  if (!entry) return null

  const view = new DataView(buffer)
  // The local header repeats the name and extra fields at its own lengths;
  // the central copy of those lengths is not reliable, so read them here.
  const nameLength = view.getUint16(entry.localHeaderOffset + 26, true)
  const extraLength = view.getUint16(entry.localHeaderOffset + 28, true)
  const start = entry.localHeaderOffset + 30 + nameLength + extraLength
  const bytes = new Uint8Array(buffer, start, entry.compressedSize)

  if (entry.method === 0) return new TextDecoder().decode(bytes)
  if (entry.method !== 8) throw new ScheduleError('unreadable_file', fileName)

  if (typeof DecompressionStream === 'undefined') {
    throw new ScheduleError('unsupported_browser', fileName)
  }
  try {
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
    return await new Response(stream).text()
  } catch {
    throw new ScheduleError('unreadable_file', fileName)
  }
}

/** The sheet the workbook lists first, falling back to the usual path. */
async function firstSheetPath(
  buffer: ArrayBuffer,
  entries: ZipEntry[],
  fileName: string,
): Promise<string> {
  const workbook = await entryText(buffer, entries, 'xl/workbook.xml', fileName)
  const rels = await entryText(buffer, entries, 'xl/_rels/workbook.xml.rels', fileName)
  if (workbook && rels) {
    const sheet = parseXml(workbook, fileName).getElementsByTagName('sheet')[0]
    const id = sheet?.getAttribute('r:id') ?? sheet?.getAttribute('id')
    if (id) {
      for (const node of Array.from(parseXml(rels, fileName).getElementsByTagName('Relationship'))) {
        if (node.getAttribute('Id') !== id) continue
        const target = node.getAttribute('Target') ?? ''
        const path = target.startsWith('/')
          ? target.slice(1)
          : `xl/${target.replace(/^\.\//, '')}`
        if (entries.some((entry) => entry.name === path)) return path
      }
    }
  }
  const firstWorksheet = entries.find((entry) => entry.name.startsWith('xl/worksheets/sheet'))
  if (!firstWorksheet) throw new ScheduleError('unreadable_file', fileName)
  return firstWorksheet.name
}

function parseXml(text: string, fileName: string): Document {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new ScheduleError('unreadable_file', fileName)
  }
  return doc
}

function sharedStrings(xml: string, fileName: string): string[] {
  const doc = parseXml(xml, fileName)
  return Array.from(doc.getElementsByTagName('si')).map((si) => textOf(si))
}

/** Concatenates the runs of a string item, dropping phonetic hints. */
function textOf(node: Element): string {
  let out = ''
  for (const t of Array.from(node.getElementsByTagName('t'))) {
    if (t.parentElement?.tagName === 'rPh') continue
    out += t.textContent ?? ''
  }
  return out
}

function rowsOf(xml: string, shared: string[], fileName: string): SheetRow[] {
  const doc = parseXml(xml, fileName)
  const rows: SheetRow[] = []

  for (const row of Array.from(doc.getElementsByTagName('row'))) {
    const number = Number(row.getAttribute('r') ?? 0)
    const cells: Record<string, string> = {}

    for (const cell of Array.from(row.getElementsByTagName('c'))) {
      const reference = cell.getAttribute('r') ?? ''
      const column = reference.replace(/\d+/g, '')
      const value = cellText(cell, shared)
      if (value !== '') cells[column] = value
    }

    if (Object.keys(cells).length > 0) rows.push({ row: number, cells })
  }

  if (rows.length === 0) throw new ScheduleError('empty_file', fileName)
  return rows
}

function cellText(cell: Element, shared: string[]): string {
  const type = cell.getAttribute('t')
  if (type === 'inlineStr') {
    const inline = cell.getElementsByTagName('is')[0]
    return inline ? textOf(inline).trim() : ''
  }

  const value = cell.getElementsByTagName('v')[0]?.textContent
  if (value == null) return ''
  if (type === 's') return (shared[Number(value)] ?? '').trim()
  return value.trim()
}

/**
 * An Excel serial as its wall-clock parts.
 *
 * Two exports of the same report can disagree about whether a date cell is
 * text or a serial, so the reader accepts both rather than failing on the
 * second one. Day 60 does not exist — the 1900 system keeps Lotus's phantom
 * leap day — so serials at or below it are read one day earlier.
 */
export function fromSerial(serial: number): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
} {
  const days = Math.floor(serial)
  const fraction = serial - days
  const epochDays = days - (days > 60 ? 25569 : 25568)
  const date = new Date(epochDays * 86_400_000)
  // Rounded to the minute: a serial carries the time as a binary fraction, so
  // 08:00 can arrive as 07:59:59.999.
  const minutes = Math.round(fraction * 1440)
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: Math.floor(minutes / 60) % 24,
    minute: minutes % 60,
  }
}
